import type { FollowerSkill, FollowerTemplate, FollowerTrait } from '../schemas';
import { FOLLOWER_ABILITY_IDS } from './followerPeculiarities';

/**
 * **D8b — `applyPrimarySkillFromNarrativeField`**: costruisce la skill
 * iniziale derivata dal `primarySkillFromNarrativeField` del template
 * (Braccio Destro Warlock: Abilità primaria scelta dal giocatore via
 * Select oneOf 9 Abilità). Ritorna array vuoto se il template non ha il
 * campo definito; lancia error descrittivo se il narrativeField è vuoto o
 * contiene un valore non valido.
 *
 * Estratto da `followerCreation.ts` per rispettare `max-lines: 400`.
 */
export function applyPrimarySkillFromNarrativeField(
  template: FollowerTemplate,
  narrativeFieldValues: Readonly<Record<string, string>>,
): FollowerSkill[] {
  const config = template.primarySkillFromNarrativeField;
  if (!config) return [];
  const raw = narrativeFieldValues[config.narrativeFieldId]?.trim() ?? '';
  if (raw.length === 0) {
    throw new Error(
      `Template '${template.id}': primarySkillFromNarrativeField richiede NarrativeField '${config.narrativeFieldId}' compilato`,
    );
  }
  if (!FOLLOWER_ABILITY_IDS.includes(raw as (typeof FOLLOWER_ABILITY_IDS)[number])) {
    throw new Error(
      `Template '${template.id}': primarySkillFromNarrativeField richiede una FollowerAbilityId valida; ricevuto '${raw}'`,
    );
  }
  return [
    {
      abilityId: raw as (typeof FOLLOWER_ABILITY_IDS)[number],
      rank: config.rank,
      isPrimary: true,
      movesUsedThisCamp: [],
      movesUsedThisSession: [],
    },
  ];
}

/**
 * **D8b — `applyTraitFromNarrativeFields`**: costruisce il trait
 * sempre-presente derivato dal `traitFromNarrativeFields` del template
 * (Braccio Destro Warlock: 1 capacità utile concordata col GM). Ritorna
 * array vuoto se il template non ha il campo definito; lancia error
 * descrittivo se la `label` è vuota.
 *
 * Estratto da `followerCreation.ts` per rispettare `max-lines: 400`.
 */
export function applyTraitFromNarrativeFields(
  template: FollowerTemplate,
  narrativeFieldValues: Readonly<Record<string, string>>,
): FollowerTrait[] {
  const config = template.traitFromNarrativeFields;
  if (!config) return [];
  const label = narrativeFieldValues[config.labelFieldId]?.trim() ?? '';
  if (label.length === 0) {
    throw new Error(
      `Template '${template.id}': traitFromNarrativeFields richiede NarrativeField '${config.labelFieldId}' compilato (label)`,
    );
  }
  const description = config.descriptionFieldId
    ? (narrativeFieldValues[config.descriptionFieldId]?.trim() ?? '')
    : '';
  return [
    {
      id: config.traitId,
      label,
      ...(description.length > 0 ? { description } : {}),
    },
  ];
}
